import { FormEvent, ReactNode, useEffect } from "react";

import { useModalHistoryEntry } from "../../lib/modalHistory";

type Size = "md" | "lg" | "xl";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: Size;
  testId?: string;
  /** When set, content is wrapped in <form className="modal"> */
  onSubmit?: (event: FormEvent) => void;
  className?: string;
  /** Keep modal mounted/in history but hide under a higher overlay. */
  obscured?: boolean;
  /** Nie rejestruj w historii wstecz (dla modalów zagnieżdżonych nad innym). */
  skipHistory?: boolean;
};

export function FormModal({
  open,
  onClose,
  title,
  children,
  size = "md",
  testId,
  onSubmit,
  className,
  obscured = false,
  skipHistory = false,
}: Props) {
  useModalHistoryEntry(skipHistory ? false : open, onClose);

  useEffect(() => {
    if (!open || obscured) {
      return;
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, obscured, onClose]);

  if (!open) {
    return null;
  }

  const sizeClass = size === "md" ? "" : ` modal--${size}`;
  const modalClass = `modal${sizeClass}${className ? ` ${className}` : ""}`;

  return (
    <div
      className={`modal-overlay${obscured ? " modal-overlay--obscured" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-hidden={obscured || undefined}
      aria-labelledby="form-modal-title"
      data-testid={testId}
      onClick={(event) => {
        if (obscured) {
          return;
        }
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      {onSubmit ? (
        <form
          className={modalClass}
          autoComplete="off"
          onClick={(event) => event.stopPropagation()}
          onSubmit={onSubmit}
        >
          <h3 className="modal__title" id="form-modal-title">
            {title}
          </h3>
          {children}
        </form>
      ) : (
        <div className={modalClass} onClick={(event) => event.stopPropagation()}>
          <h3 className="modal__title" id="form-modal-title">
            {title}
          </h3>
          {children}
        </div>
      )}
    </div>
  );
}
